import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
} from 'react';
import i18next from 'i18next';
import _ from 'lodash';
import { useFormContext } from 'react-hook-form';
import AwesomeDebouncePromise from 'awesome-debounce-promise';

import useStyles from 'style/js-style/containers/administration/calculator/CalculatorDialogStyle';
import {
  CalculatorContext,
  CalculationTargetResourceType,
  CalculationResultType,
  ExecutionMode,
  getCalculationNatureOptions,
  getCalculationTargetResourceTypeOptions,
  getCalculationResultTypeOptions,
  getExecutionModeOptions,
} from 'services/common/CalculatorUtils';
import { isCalculationNameAuthorized } from 'services/edge/CalculatorService';
import { Context } from 'states/Store';
import { ERROR } from 'services/common/Constants';
import EAButton from 'components/common/EAButton';
import EAColorIcon from '../../../../../components/common/EAColorIcon';
import CalculatorStandardInput from '../../form/calculatorInputs/CalculatorStandardInput';
import CalculatorSelectInput from '../../form/calculatorInputs/CalculatorSelectInput';

const InitialStep = ({
  calculation = null,
  isEdition = false,
  onCancel,
  onContinue,
  readOnly = false,
  setExecutionMode,
}) => {
  const { classes } = useStyles();
  const [state, dispatch] = useContext(Context);
  const [availableContextModels, setAvailableContextModels] = useState([]);
  const [availableDossierTypes, setAvailableDossierTypes] = useState([]);
  const [calculationNatureOptions] = useState(getCalculationNatureOptions());
  const [calculationScopeOptions] = useState(getCalculationTargetResourceTypeOptions());
  const [availableTargetResourceKeyOptions, setAvailableTargetResourceKeyOptions] = useState();
  const [calculationResultTypeOptions] = useState(
    getCalculationResultTypeOptions()
  );
  const [executionModeOptions] = useState(getExecutionModeOptions());
  const [isContinueClicked, setIsContinueClicked] = useState(false);

  const [hasErrors, setHasErrors] = useState(false);

  const {
    dossierTypes,
    contextModels
  } = useContext(CalculatorContext);

  const formMethods = useFormContext();
  const {
    formState,
    trigger,
    setError,
    clearErrors,
    watch,
    setValue,
    getValues,
  } = formMethods;
  const currentScopeType = watch('targetResourceType');

  const isValueAuthorized = async (value) => {
    try {
      const resp = await isCalculationNameAuthorized(
        state.account.id,
        value,
        null
      );

      return !!resp.content;
    } catch (error) {
      const err =
        error?.message?.messages?.error && error?.message?.messages?.error[0];
      dispatch({
        type: 'ALERT',
        alert: { type: ERROR, msg: err?.code || 'INTERNAL_ERROR' },
      });
    }
  };


  const validateCalculationName = AwesomeDebouncePromise(
    async (value, oldValue, fieldName) => {
      const CALCULATION_NAME_MAX_LENGTH = 100;

      let isValid = false;
      let typeErrMsg = '';

      if (oldValue && oldValue === value) {
        isValid = true;
      } else {
        if (value.length === 0) {
          typeErrMsg = i18next.t('infos.required');
        } else if (value.length > CALCULATION_NAME_MAX_LENGTH) {
          typeErrMsg = i18next.t(
            'calculator.errors.calculationNameMaxLengthExceeded',
            {
              length: CALCULATION_NAME_MAX_LENGTH,
            }
          );
        } else {
          let isCalculationNameUnique = await isValueAuthorized(value);
          if (!isCalculationNameUnique) {
            typeErrMsg = i18next.t('calculator.errors.nameNotUnique');
          } else {
            isValid = true;
          }
        }
      }

      if (isValid) {
        clearErrors(fieldName);
      } else {
        setError(fieldName, { type: 'manual', message: typeErrMsg });
        return typeErrMsg;
      }
    },
    500
  );

  const validateCalculationDescription = AwesomeDebouncePromise(
    async (value, fieldName) => {
      const CALCULATION_DESC_MAX_LENGTH = 300;

      let isValid = false;
      let typeErrMsg = '';

      if (value.length > CALCULATION_DESC_MAX_LENGTH) {
        typeErrMsg = i18next.t('calculator.errors.descMaxLengthExceeded', {
          length: CALCULATION_DESC_MAX_LENGTH,
        });
      } else {
        isValid = true;
      }

      if (isValid) {
        clearErrors(fieldName);
      } else {
        setError(fieldName, { type: 'manual', message: typeErrMsg });
        return typeErrMsg;
      }
    },
    500
  );

  const getFilteredCalculationResultTypeOptions = (executionMode) => {
    if (executionMode === ExecutionMode.SUM_OF_MATCHING_BRANCH_RESULTS) {
      return calculationResultTypeOptions.filter(
        (o) => o.value === CalculationResultType.ARITHMETIC_FORMULA_OUTPUT
      );
    }
    return calculationResultTypeOptions;
  };

  const getAvailableTargetResourceKeyOptions = useCallback(() => {
    let options = []

    switch(currentScopeType) {

      case CalculationTargetResourceType.CONTEXT:
          options = availableContextModels;
        break;

      case CalculationTargetResourceType.DOSSIER:
        options = availableDossierTypes;
        break;

      default:
        break;
    }
    
    return options;
  }, [currentScopeType, availableContextModels, availableDossierTypes]);


  const onCalculationTargetResourceTypeChange = useCallback((newValue) => {
    setValue(`targetResourceKeys`, []);
    setAvailableTargetResourceKeyOptions(getAvailableTargetResourceKeyOptions());
  }, [setValue, getAvailableTargetResourceKeyOptions]);

  useEffect(() => {
    if (contextModels) {
      setAvailableContextModels([
        ...contextModels
          .filter((model) => model.active)
          .map((c) => {
            return {
              value: c.id,
              label: c.name,
            };
          }),
      ]);
    }
  }, [contextModels]);

  useEffect(() => {    
    if (dossierTypes) {
      setAvailableDossierTypes([
        ...dossierTypes.map((c) => {
            return {
              //Context dossier type values are Strings, let's parse it 
              value: JSON.parse(c.value),
              label: c.label,
            };
          }),
      ]);
    }
  }, [dossierTypes]);

  useEffect(() => {
    setHasErrors(
      !!_.get(formState.errors, `name`) ||
      !!_.get(formState.errors, `nature`) ||
      !!_.get(formState.errors, `resultType`) ||
      !!_.get(formState.errors, `executionMode`) ||
      !!_.get(formState.errors, `description`) ||
      !!_.get(formState.errors, `targetResourceKeys`)
    );
  }, [formState]);

  // We continue to next step if the validation is done and no errors are found.
  useEffect(() => {
    if (isContinueClicked && !hasErrors) {
      onContinue();
    }

    setIsContinueClicked(false);
  }, [isContinueClicked, hasErrors, onContinue]);

  useEffect(() => {
    setAvailableTargetResourceKeyOptions(getAvailableTargetResourceKeyOptions());
  }, [getAvailableTargetResourceKeyOptions]);


  return (
    <>
      {/* Title */}
      <EAColorIcon icon="calculator/create-pop-up" />
      <h2 className={classes.title}>
        {readOnly
          ? calculation?.name || i18next.t("read")
          : isEdition
            ? i18next.t('calculator.dialog.edit.title')
            : i18next.t('calculator.dialog.create.title')
        }
      </h2>

      {/* Main content */}
      <div className={classes.mainContentWrapper}>
        {/* Name */}
        <div className={classes.formWrapper} style={{ gap: '15px' }}>
          <CalculatorStandardInput
            fieldName={`name`}
            label={`${i18next.t('calculator.dialog.create.label.name')} *`}
            placeholder={`${i18next.t(
              'calculator.dialog.create.placeholder.name'
            )}`}
            isRequired={true}
            validate={async (value) => {
              return await validateCalculationName(
                value,
                calculation?.name || null,
                `name`
              );
            }}
            disabled={readOnly}
          />
        </div>

        {/* Nature */}
        <div className={classes.formWrapper} style={{ gap: '15px' }}>
          <CalculatorSelectInput
            fieldName={`nature`}
            label={`${i18next.t('calculator.dialog.create.label.nature')} *`}
            placeholder={`${i18next.t(
              'calculator.dialog.create.placeholder.nature'
            )}`}
            options={calculationNatureOptions}
            isRequired
            validate={(value) => {
              return true;
            }}
            variant="outlined"
            disabled={readOnly}
          />
        </div>
        {/* Portée */}
        <div className={classes.formWrapper} style={{ gap: '15px' }}>
          <CalculatorSelectInput
            fieldName={`targetResourceType`}
            label={`${i18next.t('calculator.dialog.create.label.portee')} *`}
            placeholder={`${i18next.t(
              'calculator.dialog.create.placeholder.portee'
            )}`}
            options={calculationScopeOptions}
            onChange={onCalculationTargetResourceTypeChange}
            validate={(value) => {
              return null;
            }}
            isRequired
            variant="outlined"
            disabled={readOnly}
          />
        </div>

        {currentScopeType !== CalculationTargetResourceType.THIRD_PARTY && availableContextModels ? (
          <div className={classes.formWrapper} style={{ gap: '15px' }}>
            <CalculatorSelectInput
              fieldName={`targetResourceKeys.0`}
              label={`${i18next.t(
                'calculator.dialog.create.label.resource'
              )} *`}
              placeholder={`${i18next.t(
                'calculator.dialog.create.placeholder.resource'
              )}`}
              options={availableTargetResourceKeyOptions}
              validate={(value) => {
                return null;
              }}
              variant="outlined"
              disabled={readOnly}
            />
          </div>
        ) : (
          <></>
        )}

        {/* Excution Mode */}
        <div className={classes.formWrapper} style={{ gap: '15px' }}>
          <CalculatorSelectInput
            fieldName={`executionMode`}
            label={`${i18next.t(
              'calculator.dialog.create.label.executionMode'
            )} *`}
            placeholder={`${i18next.t(
              'calculator.dialog.create.placeholder.executionMode'
            )}`}
            options={executionModeOptions}
            isRequired
            validate={(value) => {
              return null;
            }}
            variant="outlined"
            onChange={setExecutionMode(getValues('executionMode'))}
            disabled={readOnly}
          />
        </div>

        {/* Type de résultat */}
        <div className={classes.formWrapper} style={{ gap: '15px' }}>
          <CalculatorSelectInput
            fieldName={`resultType`}
            label={`${i18next.t(
              'calculator.dialog.create.label.resultType'
            )} *`}
            placeholder={`${i18next.t(
              'calculator.dialog.create.placeholder.resultType'
            )}`}
            options={getFilteredCalculationResultTypeOptions(
              getValues('executionMode')
            )}
            isRequired
            validate={(value) => {
              return null;
            }}
            variant="outlined"
            disabled={readOnly}
          />
        </div>

        {/* Description */}
        <div className={classes.formWrapper} style={{ gap: '15px' }}>
          <CalculatorStandardInput
            fieldName={`description`}
            label={`${i18next.t('calculator.dialog.create.label.description')}`}
            placeholder={`${i18next.t(
              'calculator.dialog.create.placeholder.description'
            )}`}
            validate={(value) => {
              return validateCalculationDescription(value, `description`);
            }}
            // propagated to the inner field
            className={classes.multilineInput}
            multiline
            rows={2}
            disabled={readOnly}
          />
        </div>
      </div>

      <div className={classes.btnWrapper}>
        <EAButton
          onClick={(e) => {
            e.preventDefault();
            onCancel();
          }}
          content={i18next.t('calculator.actions.cancel')}
          colorVariant="secondary"
        />

        <EAButton
          onClick={(e) => {
            e.preventDefault();
            trigger().then(
              (canContinue) => canContinue && setIsContinueClicked(true)
            );
          }}
          disabled={hasErrors}
          content={i18next.t('calculator.actions.continue')}
          colorVariant={'primary'}
        />
      </div>
    </>
  );
};

export default InitialStep;
